import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { trackEvent, trackFormError, trackFormStart, trackFormSubmission } from "@/lib/analytics";
import { endChat, sendChatMessage, startChat, submitChatLead } from "@/lib/api/chatClient";
import { COPY, MAX_MESSAGE_CHARS, type Chip, type Language } from "./copy";
import { joinInFlight } from "./joinInFlight";
import {
  EMPTY_CARD,
  START_RETRY_MS,
  failureText,
  initialState,
  leadNote,
  nextId,
  reduce,
  retryableStart,
  transcriptOf,
  validateCard,
  type CardErrors,
  type CardFields,
  type ChatState,
  type Mode,
} from "./chatSession";

/**
 * Wires the pure reducer to the network and to analytics. The conversation is created on
 * the server only when the panel is first opened, and the transcript is never written to
 * storage: a reload starts a fresh chat, and no visitor text sits in localStorage. The composer
 * draft and the card's fields live here too, in memory only, so closing the panel does not
 * throw away what the visitor typed.
 */

export interface ChatSessionActions {
  open: (source: "launcher" | "teaser") => void;
  close: () => void;
  send: (text: string) => Promise<void>;
  chooseChip: (chip: Chip) => void;
  requireCard: () => void;
  dismissCard: () => void;
  submitCard: (fields: CardFields) => Promise<CardErrors | null>;
}

export interface ChatDrafts {
  composer: string;
  setComposer: Dispatch<SetStateAction<string>>;
  card: CardFields;
  setCard: Dispatch<SetStateAction<CardFields>>;
}

export interface ChatSession {
  state: ChatState;
  actions: ChatSessionActions;
  drafts: ChatDrafts;
  language: Language;
}

const kindOf = (r: { failure: string; status: number }): string => (r.failure === "http" ? `http_${r.status}` : r.failure);
const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useChatSession(language: Language, page: string): ChatSession {
  const copy = COPY[language];
  const [state, dispatch] = useReducer(reduce, copy.intro, initialState);
  const [composer, setComposer] = useState("");
  const [card, setCard] = useState<CardFields>(EMPTY_CARD);

  // Everything async reads the latest state through this ref instead of a stale closure.
  const stateRef = useRef(state);
  stateRef.current = state;
  const renderTimeRef = useRef<number>(Date.now());
  const cardTrackedRef = useRef(false);
  // What the async paths need to know SYNCHRONOUSLY. `stateRef` only catches up when React
  // re-renders, and a dispatch made after an await is rendered in a later task — so code that
  // awaits start() and then reads stateRef still sees "no conversation". That is how a chip
  // clicked while the conversation was starting got silently dropped (2026-09-23): the start
  // finished, the dispatch was queued, and send() read the old state and returned. These refs are
  // written at the same moment as the dispatch, so they are never behind.
  const liveRef = useRef<{ conversationId: string | null; mode: Mode }>({ conversationId: null, mode: "ai" });
  const sendingRef = useRef(false);
  // The one in-flight start, shared, so a second caller waits for it instead of giving up.
  const startRef = useRef<Promise<{ conversationId: string | null; mode: Mode }> | null>(null);

  const start = useCallback((): Promise<{ conversationId: string | null; mode: Mode }> => {
    if (liveRef.current.conversationId) return Promise.resolve(liveRef.current);
    return joinInFlight(startRef, async () => {
      dispatch({ type: "start" });
      const attempt = () => startChat({ language, page, renderTime: renderTimeRef.current, hp: "" });
      let result = await attempt();
      // A cold or briefly unreachable Lambda usually answers a moment later. One quiet retry, so the
      // visitor is not sent to the card by a blip; a send() waiting on start() waits through it too.
      if (!result.ok && retryableStart(result)) {
        dispatch({ type: "start_retry" });
        await wait(START_RETRY_MS);
        result = await attempt();
      }
      if (result.ok) {
        liveRef.current = { conversationId: result.conversationId, mode: result.mode };
        dispatch({
          type: "started",
          conversationId: result.conversationId,
          mode: result.mode,
          turnsLeft: result.turnsLeft,
          notice: result.mode === "form" ? copy.formModeNotice : null,
        });
        if (result.mode === "form") trackEvent("chat_fallback", { chat_reason: "form_mode:start", page_language: language });
      } else {
        liveRef.current = { conversationId: null, mode: "form" };
        dispatch({ type: "start_failed", notice: copy.formModeNotice });
        trackEvent("chat_fallback", { chat_reason: `start_failed:${kindOf(result)}`, page_language: language });
      }
      return liveRef.current;
    });
  }, [copy.formModeNotice, language, page]);

  const open = useCallback(
    (source: "launcher" | "teaser") => {
      if (stateRef.current.open) return;
      dispatch({ type: "open" });
      trackEvent("chat_open", { chat_source: source, page_language: language });
      if (!liveRef.current.conversationId) void start();
    },
    [language, start],
  );

  const finish = useCallback(() => {
    const s = stateRef.current;
    if (s.ended || !s.conversationId || s.userTurns < 2) return;
    dispatch({ type: "ended" });
    endChat(s.conversationId);
  }, []);

  const close = useCallback(() => {
    dispatch({ type: "close" });
    finish();
  }, [finish]);

  // A closed tab still gets its summary written when the chat was a real one.
  useEffect(() => {
    const onHide = () => finish();
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [finish]);

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim().slice(0, MAX_MESSAGE_CHARS);
      // One message at a time, decided synchronously: two fast clicks must not both go.
      if (!text || liveRef.current.mode !== "ai") return;
      // A first message can sit waiting on a slow start (two tries, up to ~30s) while the composer and
      // chips stay live. The panel already cleared this text, so hand it back instead of dropping it.
      if (sendingRef.current) {
        setComposer((prev) => (prev.trim() ? prev : text));
        return;
      }
      sendingRef.current = true;
      try {
        // The conversation may still be starting when a chip is clicked fast: wait for it, and
        // use what start() RETURNS, never state read back after the await (see liveRef).
        const live = liveRef.current.conversationId ? liveRef.current : await start();
        // Form mode (breaker open) or a failed start: the panel is showing the lead card. The words
        // the visitor already sent stay on screen and go into the card's note, instead of vanishing.
        if (!live.conversationId || live.mode !== "ai") {
          dispatch({ type: "kept", id: nextId(), text });
          setCard((prev) => (prev.message.trim() ? prev : { ...prev, message: text }));
          return;
        }

        const turn = stateRef.current.userTurns + 1;
        const id = nextId();
        dispatch({ type: "send", id, text });
        trackEvent("chat_message_sent", { chat_turn: turn, page_language: language });

        // Never retried automatically: a timeout or a 504 may still have reached the model, and a
        // second copy would spend a turn and answer twice.
        const result = await sendChatMessage({ conversationId: live.conversationId, text });
        if (!result.ok) {
          dispatch({ type: "send_failed", id, error: failureText(result, copy, language, copy.errorNotice) });
          setComposer((prev) => (prev.trim() ? prev : text));
          trackEvent("chat_fallback", { chat_reason: `send_failed:${kindOf(result)}`, page_language: language });
          return;
        }
        if (result.mode === "ai") {
          dispatch({ type: "reply", id: nextId(), text: result.reply, turnsLeft: result.turnsLeft });
          // The last allowed turn: say so now instead of letting the next message bounce.
          if (result.turnsLeft <= 0) {
            liveRef.current = { ...liveRef.current, mode: "form" };
            dispatch({ type: "form_mode", id: nextId("n"), notice: copy.capNotice, turnsLeft: 0 });
            trackEvent("chat_fallback", { chat_reason: "form_mode:turn-cap", page_language: language });
          }
          return;
        }
        liveRef.current = { ...liveRef.current, mode: "form" };
        trackEvent("chat_fallback", { chat_reason: `form_mode:${result.reason}`, page_language: language });
        dispatch({
          type: "form_mode",
          id: nextId("n"),
          notice: result.reason === "turn-cap" ? copy.capNotice : copy.formModeNotice,
          turnsLeft: result.turnsLeft,
        });
      } finally {
        sendingRef.current = false;
      }
    },
    [copy, language, start],
  );

  const requireCard = useCallback(() => dispatch({ type: "require_card" }), []);
  const dismissCard = useCallback(() => dispatch({ type: "dismiss_card" }), []);

  const chooseChip = useCallback(
    (chip: Chip) => {
      if (chip.id === "talk") {
        requireCard();
        return;
      }
      if (chip.message) void send(chip.message);
      // "book" is a plain link; the delegated link tracker records it as a cta_click.
    },
    [requireCard, send],
  );

  // lead_form_start once, the first time the card is on screen.
  useEffect(() => {
    if (cardTrackedRef.current) return;
    if (state.card === "offered" || state.card === "required") {
      cardTrackedRef.current = true;
      trackFormStart("chat_widget", language);
    }
  }, [language, state.card]);

  const submitCard = useCallback(
    async (fields: CardFields): Promise<CardErrors | null> => {
      const errors = validateCard(fields, copy);
      if (Object.keys(errors).length > 0) {
        trackFormError("chat_widget", `validation:${Object.keys(errors).join(",")}`, language);
        return errors;
      }
      const s = stateRef.current;
      dispatch({ type: "card_submit" });
      const message = leadNote(fields, s.messages, copy);
      // Unsent words already ride in the note; do not repeat them underneath.
      const transcript = transcriptOf(s.messages.filter((m) => !(m.unsent && message.includes(m.text))));
      const result = await submitChatLead({
        conversationId: liveRef.current.conversationId,
        language,
        name: fields.name.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim(),
        business: fields.business.trim(),
        message,
        hp: fields.hp,
        transcript,
      });
      if (result.fallbackFrom) {
        trackEvent("chat_fallback", { chat_reason: `lead_fallback:${result.fallbackFrom}`, page_language: language });
      }
      if (!result.ok) {
        dispatch({ type: "card_failed", error: failureText(result, copy, language) });
        trackFormError("chat_widget", `${result.failure}:${result.status}`, language);
        return null;
      }
      dispatch({ type: "card_saved", id: nextId("n"), text: copy.saved, topic: fields.about || null });
      trackFormSubmission("chat_widget", language, {
        chat_mode: s.mode,
        chat_turns: s.userTurns,
        chat_via: result.via,
      });
      return null;
    },
    [copy, language],
  );

  const actions = useMemo<ChatSessionActions>(
    () => ({ open, close, send, chooseChip, requireCard, dismissCard, submitCard }),
    [open, close, send, chooseChip, requireCard, dismissCard, submitCard],
  );

  const drafts = useMemo<ChatDrafts>(() => ({ composer, setComposer, card, setCard }), [composer, card]);

  return { state, actions, drafts, language };
}
